import { LEADS_API } from "@/constants/content";

const DEFAULT_BASE = "https://propvision-backend-production.up.railway.app";
const DEFAULT_CLIENT_ID = "client_buildaxis_main";

/** Align with PropVision backend normalize_phone (+ prefix, no spaces). */
export function normalizeLeadPhone(phone) {
  const cleaned = String(phone || "")
    .trim()
    .replace(/[\s-()]/g, "");
  if (!cleaned) return "";
  if (cleaned.startsWith("+")) return cleaned;
  if (cleaned.startsWith("00")) return `+${cleaned.slice(2)}`;
  if (cleaned.startsWith("91") && cleaned.length >= 12) return `+${cleaned}`;
  if (/^\d{10}$/.test(cleaned)) return `+91${cleaned}`;
  return `+${cleaned}`;
}

function resolveLeadsBaseUrl() {
  const raw = (LEADS_API.baseUrl || DEFAULT_BASE).trim();
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/$/, "");
  return DEFAULT_BASE;
}

/**
 * Build payload for POST /leads/website.
 *
 * Live Mongo has a unique index on (client_id, email). Sending an email already
 * tied to another phone returns plain "Internal Server Error" (500). Until the
 * PropVision upsert fix is deployed, keep email out of the email field and
 * append it to message instead so the lead still saves by phone.
 */
function buildWebsiteLeadBody(payload) {
  const clientId =
    (LEADS_API.clientId || DEFAULT_CLIENT_ID).trim().replace(/^["']|["']$/g, "") ||
    DEFAULT_CLIENT_ID;

  const name = payload.name?.trim();
  const phone = normalizeLeadPhone(payload.phone);
  const email = payload.email?.trim();
  const budget = payload.budget?.trim();
  const baseMessage = payload.message?.trim();

  const messageParts = [baseMessage];
  if (email) messageParts.push(`Email: ${email}`);
  if (budget) messageParts.push(`Budget: ${budget}`);
  const message = messageParts.filter(Boolean).join("\n\n");

  const body = { client_id: clientId };
  if (name) body.name = name;
  if (phone) body.phone = phone;
  if (message) body.message = message;
  // Intentionally omit body.email / body.budget — unique email index 500s on live.

  return body;
}

export async function submitWebsiteLead(payload) {
  const url = `${resolveLeadsBaseUrl()}/leads/website`;
  const body = buildWebsiteLeadBody(payload);

  if (!body.phone && !payload.email?.trim()) {
    throw new Error("Please share your phone number or email.");
  }

  if (!body.phone) {
    throw new Error("Please share your phone number.");
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
    mode: "cors",
    credentials: "omit",
  });

  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    console.error("[leads/website] failed", res.status, text, body);
    const detail = data?.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg || d).join(", ")
          : data?.message || (text && text.length < 200 ? text : null) || `Request failed (${res.status})`;
    throw new Error(msg);
  }

  return data;
}
